import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from "@angular/core";
import * as _ from "lodash";
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragEnter,
  CdkDropList
} from "@angular/cdk/drag-drop";
import {RoutePanelModel} from "../../../models/route-panel.model";
import {RoutesViewSettingsModel, RoutesViewSettingsRouteModel} from "../../../models/routes-view-settings.model";
import {TaskModel} from "../../../../tasks/models/task.model";
import {TaskType} from "../../../../tasks/types/task.type";
import {TaskPurposeType} from "../../../../commons/types/task-purpose.type";
import {dedupeRelationsByService} from "../../../../tasks/utils/task-relations.util";
import {TaskRelationModel} from "../../../../tasks/models/task-relation.model";
import {RouteModel} from "../../../models/route.model";
import {RoutesComponentStore} from "../../state/routes.store";
import {Observable, Subject, Subscription} from "rxjs";
import {debounceTime} from "rxjs/operators";
import {Service} from "../../../../deliveries/models/service.model";
import {ServiceType} from "../../../../deliveries/types/service.type";
import {AccountReferenceModel} from "../../../../account/models/account-reference.model";
import {PanelService} from "../../../../commons/angular/services/panel.service";
import {
  TaskRelationsPopupComponent,
  TaskRelationsPopupData
} from "../../../../commons/angular/components/task-relations-popup/task-relations-popup.component";
import {TasksHttpService} from "../../../../tasks/angular/services/tasks-http.service";
import {BringozMenuPanelComponent} from "../../../../commons/angular/components/bringoz-menu-panel/bringoz-menu-panel.component";
import {RoutesDragAndDropService} from "../../services/routes-drag-and-drop.service";
import {DEFAULT_ROUTE_DRILLDOWN_COLUMN_WIDTHS} from "../../../constants/routes-column-widths.constant";
import {ROUTE_DRILLDOWN_COLUMN_GROUPS} from "../../../constants/routes-column-groups.constant";

const DEBOUNCE_TIME: number = 150;

require('./routes-table-route-view.scss');
@Component({
  standalone: false,
  selector: 'app-routes-table-route-view',
  templateUrl: './routes-table-route-view.html',
  providers: [RoutesComponentStore],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RoutesTableRouteViewComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input() index: any;
  @Input() panel: RoutePanelModel;
  @Input() route: RouteModel;
  @Input() panels: RoutePanelModel[];
  @Input() settings: RoutesViewSettingsModel;
  @Input() visibleIds: string[];
  @Input() selectedServiceIds: string[];
  @Input() highlightedActivity?: any;
  @Input() isLoading: boolean;
  @Input() ungroupedRouteIds: Set<string>;
  @Input() expandedRoutes: any[] = [];
  @Input() resourcesDropListIds?: string[];
  @Input() routesDropListIds?: string[];
  @Output() onSelect = new EventEmitter();
  @Output() onViewJob = new EventEmitter<boolean>();
  @Output() onVisibleRoutesChange = new EventEmitter<string[]>();
  @Output() onLockRouteChange = new EventEmitter<{id: string, locked: boolean}>();
  @Output() onMoveTask = new EventEmitter();
  @Output() onAddStopsToRoute = new EventEmitter();
  @Output() onToggleVisible = new EventEmitter<{route: RouteModel, isVisible: boolean}>();
  @Output() onSelectServices = new EventEmitter<string[]>();
  @Output() onSplitByPackages = new EventEmitter<{ item: Service | TaskModel; event: MouseEvent }>();
  @Output() onSelectRouteResource = new EventEmitter<{route: RouteModel, event: MouseEvent}>();
  @Output() onSettingsChange = new EventEmitter<RoutesViewSettingsRouteModel>();

  get uniqueId(): string {
    return 'route-' + this.panel?.id;
  }

  public defaultWidths = DEFAULT_ROUTE_DRILLDOWN_COLUMN_WIDTHS;
  public columnGroups = ROUTE_DRILLDOWN_COLUMN_GROUPS;

  @ViewChild('headerContainer', {static: false}) private headerContainer: ElementRef;
  @ViewChild('bodyContainer', {static: false}) private bodyContainer: ElementRef;
  @ViewChild('summaryContainer', {static: false}) private summaryContainer: ElementRef;

  public mutableRoute: any;
  public tasks: TaskModel[] = [];
  public allPanelIds: string[];
  public TaskType = TaskType;
  public TaskPurposeType = TaskPurposeType;
  public selectedKeys: Object;
  public visibleKeys: Object;
  public taskIndexOffset: number = 1;
  public localHighlightedTaskIds = new Set<string>();
  public hoveredTaskId?: string;

  // Precomputed maps for stops/tasks in table view
  public purposeToServiceType: any;
  public isSplitAllowedMap: {[taskId: string]: boolean} = {};
  public accountReferencesMap: {[taskId: string]: (AccountReferenceModel & { pallets?: number })[]} = {};
  public proNumbersMap: {[taskId: string]: string[]} = {};
  public relationsMap: {[taskId: string]: TaskRelationModel[]} = {};
  public programs: { [key: string]: string } = {};

  public loading$: Observable<boolean>;
  private routeSubscription: Subscription;
  private debounceSubscription: Subscription;
  private routeUpdate$ = new Subject<RouteModel>();
  private resizeObserver: ResizeObserver;
  private _contextMenuSubscription: Subscription;

  constructor(
    @Inject(RoutesComponentStore) private routesStore: RoutesComponentStore,
    @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef,
    @Inject('$timeout') private $timeout,
    @Inject('planningAlertsService') private planningAlertsService,
    @Inject('routesService') private routesService,
    @Inject('splitService') private splitService,
    @Inject(PanelService) private panelService: PanelService,
    @Inject('servicesService') private servicesService,
    @Inject(TasksHttpService) private tasksHttpService: TasksHttpService,
    @Inject(RoutesDragAndDropService) private routesDragAndDropService: RoutesDragAndDropService) {
      this.purposeToServiceType = {
        [TaskPurposeType.Pickup]: ServiceType.Delivery,
        [TaskPurposeType.Dropoff]: ServiceType.Delivery,
        [TaskPurposeType.Operation]: ServiceType.Operation
      };
      this.programs = this.splitService.getSiteProgramTypesMap();
  }

  ngOnInit() {
    this.sortPredicate = this.sortPredicate.bind(this);

    this.routesStore.setRoutes([this.route]);
    this.routesStore.selectRoute(this.route.id);

    this.loading$ = this.routesStore.loading$;

    this.debounceSubscription = this.routeUpdate$
      .pipe(debounceTime(DEBOUNCE_TIME))
      .subscribe((route) => {
        if (!this.mutableRoute) {
          this.mutableRoute = { ...route };
          this.routesStore.subscribeTasks(_.map(route.routeTaskList, 'id'));
          this.tasks = this.routesService.getTaskList(this.mutableRoute.routeTaskList, !this.ungroupedRouteIds.has(this.mutableRoute.id));
        } else {
          const tasksToAdd = _.differenceBy(route.routeTaskList, this.mutableRoute.routeTaskList, 'id');
          const tasksToRemove = _.differenceBy(this.mutableRoute.routeTaskList, route.routeTaskList, 'id');

          this.mutableRoute = { ...route };
          this.tasks = this.routesService.getTaskList(this.mutableRoute.routeTaskList, !this.ungroupedRouteIds.has(this.mutableRoute.id));

          if (tasksToRemove.length) {
            this.routesStore.unsubscribeTasks(_.map(tasksToRemove, 'id'));
          }

          if (tasksToAdd.length) {
            this.routesStore.subscribeTasks(_.map(tasksToAdd, 'id'));
          }
        }

        this.taskIndexOffset = this.mutableRoute?.routeTaskList?.[0]?.taskType === TaskType.Internal ? 0 : 1;
        this.routesStore.setLoading(false);
        this._precomputeTaskHelpers();
        this.cdr.detectChanges();
        setTimeout(() => {
          this.syncHeaderScrollbarWidth();
        }, 0);
      });

    this.routeSubscription = this.routesStore.selectedRoute$.subscribe((route: RouteModel) => {
      this.routeUpdate$.next(route);
    });

    this.resizeObserver = new ResizeObserver(() => {
      this.syncHeaderScrollbarWidth();
    });
  }

  ngAfterViewInit() {
    if (this.bodyContainer) {
      this.resizeObserver.observe(this.bodyContainer.nativeElement);

      const innerWrapper = this.bodyContainer.nativeElement.querySelector('.table-inner-wrapper');
      if (innerWrapper) {
        this.resizeObserver.observe(innerWrapper);
      }
    }
    this.syncHeaderScrollbarWidth();
  }

  ngOnDestroy() {
    this.routeSubscription?.unsubscribe();
    this.debounceSubscription?.unsubscribe();
    this._contextMenuSubscription?.unsubscribe();
    this.routesStore.unSubscribeAll();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  onRightClick(event: MouseEvent, task: TaskModel) {
    if (task.taskType === TaskType.Internal) {
      return;
    }
    event.preventDefault();
    const panel = this.panelService.open(BringozMenuPanelComponent, event, [
      {
        name: 'Split Packages',
        action: 'splitByPackages',
        iconName: 'BOX',
        disabled: !this.isSplitAllowedMap[task.id]
      }], {
      position: {
        originX: 'center',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'top',
      },
      width: 130
    });

    if (panel) {
      if (this._contextMenuSubscription) {
        this._contextMenuSubscription.unsubscribe();
      }

      this._contextMenuSubscription = panel.instance.onClose
        .subscribe((action?: string) => {
          switch (action) {
            case 'splitByPackages':
              this.onSplitByPackages.emit({ item: task, event: event });
              break;
            default:
              break;
          }

          this._contextMenuSubscription?.unsubscribe();
        });
    }
  }

  onRelationsClick(event: MouseEvent, task: TaskModel) {
    const relations = this.relationsMap[task.id] || [];
    this.panelService.open<TaskRelationsPopupComponent>(TaskRelationsPopupComponent, event, {
      relations,
      tasks$: this.tasksHttpService.getTasks([task.id])
    } as TaskRelationsPopupData);
  }

  onBodyScroll(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.headerContainer) {
      this.headerContainer.nativeElement.scrollLeft = target.scrollLeft;
    }
    if (this.summaryContainer) {
      this.summaryContainer.nativeElement.scrollLeft = target.scrollLeft;
    }
  }

  onToggleLockedRoute(route: RouteModel) {
    this.onLockRouteChange.emit({ id: route.id, locked: !route.isBlockedByUser });
  }

  private syncHeaderScrollbarWidth() {
    if (this.headerContainer && this.bodyContainer) {
      const bodyEl = this.bodyContainer.nativeElement;
      const headerEl = this.headerContainer.nativeElement;
      const scrollbarWidth = bodyEl.offsetWidth - bodyEl.clientWidth;
      headerEl.style.paddingRight = `${scrollbarWidth}px`;
      if (this.summaryContainer) {
        const summaryEl = this.summaryContainer.nativeElement;
        summaryEl.style.paddingRight = `${scrollbarWidth}px`;

        const summaryRow = summaryEl.querySelector('.routes-table-summary-row');
        if (summaryRow) {
          const summaryWidth = summaryRow.scrollWidth;
          const innerWrappers = [
            headerEl.querySelector('.table-inner-wrapper'),
            bodyEl.querySelector('.table-inner-wrapper')
          ];
          innerWrappers.forEach(wrapper => {
            if (wrapper) {
              wrapper.style.minWidth = `${summaryWidth}px`;
            }
          });
        }
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.panels || changes.resourcesDropListIds || changes.routesDropListIds) {
      const drilldownLists = [];
      const filteredPanels = _.filter(this.panels, (p) => {
        if (p.id === this.panel.id) {
          return false;
        }
        if (p.selectedTabIndex === 0) {
          drilldownLists.push(`${p.id}-drilldown`);
        }
        return true;
      })
      this.allPanelIds = _.concat(
        _.map(filteredPanels, 'id'),
        this.resourcesDropListIds || [],
        this.routesDropListIds || [],
        drilldownLists
      );
    }
    if (changes.visibleIds) {
      this.visibleKeys = {};
      _.forEach(this.visibleIds, (id) => {
        this.visibleKeys[id] = true;
      });
    }
    if (changes.selectedServiceIds) {
      this.selectedKeys = {};
      this.selectedServiceIds?.forEach((id) => {
        const tasks = this.tasks.filter((t) => {
          return t.bundleServiceIdList ? _.includes(t.bundleServiceIdList, id) : (t.serviceId === id);
        });

        tasks.forEach((a) => {
          this.selectedKeys[a.id] = true;
        });
      });
    }
    if (changes.route) {
      this.routesStore.setRoutes([this.route]);
      this.routesStore.selectRoute(this.route.id);
    }

    if (changes.ungroupedRouteIds && this.mutableRoute?.routeTaskList) {
      this.tasks = this.routesService.getTaskList(this.mutableRoute.routeTaskList, !this.ungroupedRouteIds.has(this.mutableRoute.id));
      this._precomputeTaskHelpers();
    }
  }

  onToggleVisibleRoute(id, isVisible: boolean) {
    this.visibleKeys[id] = isVisible;
    const visibleIds = _.map(this.visibleKeys, (value, key) => {
      if (value) return key;
    });

    this.onVisibleRoutesChange.emit(_.compact(visibleIds));
  }

  onSelectedChange(task: any, isSelected: boolean) {
    let selectedIds;
    let serviceIds = task.bundleServiceIdList || [task.serviceId];

    if (isSelected) {
      selectedIds = _.uniq([...this.selectedServiceIds].concat(serviceIds));
    } else {
      selectedIds = this.selectedServiceIds.filter(id => !serviceIds.includes(id));
    }

    this.onSelectServices.emit(_.compact(selectedIds));
  }

  onHoverTask(task: TaskModel, isHover: boolean) {
    this.localHighlightedTaskIds.clear();
    if (isHover) {
      this.hoveredTaskId = task.id;
      const counterpartIds = this.getCounterpartTaskIds(task);
      counterpartIds.forEach(id => this.localHighlightedTaskIds.add(id));
    } else {
      this.hoveredTaskId = undefined;
    }
    this.cdr.detectChanges();
  }

  private getCounterpartTaskIds(task: TaskModel): string[] {
    return this.routesService.getCounterpartTaskIds(task, this.tasks);
  }

  sortPredicate(index: number, item: CdkDrag<any>, drop: CdkDropList) {
    if (!!item.data?.serviceType) {
      return this._sortPredicateService(index, item, drop);
    } else {
      return this._sortPredicateTask(index, item, drop);
    }
  }

  enter(event: CdkDragEnter) {

  }

  drop(event: CdkDragDrop<string[]>) {
    this.routesDragAndDropService.onDropRoute(
      this.mutableRoute,
      event,
      this.selectedServiceIds,
      this.expandedRoutes,
      this.tasks
    ).then((result) => {
      if (result) {
        this.onMoveTask.emit(result.data);
      }
    });
  }

  private _sortPredicateService(index: number, item: CdkDrag<any>, drop: CdkDropList) {
    const startIndex = _.findIndex(drop.data, (t: any) => {
      return t.taskType === TaskType.Internal && t.taskSpec.purpose === TaskPurposeType.StartRoute;
    })
    const endIndex = _.findIndex(drop.data, (t: any) => {
      return t.taskType === TaskType.Internal && t.taskSpec.purpose === TaskPurposeType.EndRoute;
    })

    if (((startIndex > -1) && (startIndex >= index)) || ((endIndex > -1) && endIndex <= index)) {
      return false;
    } else {
      return true;
    }
  }

  private _sortPredicateTask(index: number, item: CdkDrag<any>, drop: CdkDropList) {
    const purpose = item.data.taskSpec.purpose

    const startIndex = _.findIndex(drop.data, (t: any) => {
      return t.taskType === TaskType.Internal && t.taskSpec.purpose === TaskPurposeType.StartRoute;
    })
    const endIndex = _.findIndex(drop.data, (t: any) => {
      return t.taskType === TaskType.Internal && t.taskSpec.purpose === TaskPurposeType.EndRoute;
    })

    if (((startIndex > -1) && (startIndex >= index)) || ((endIndex > -1) && endIndex <= index)) {
      return false;
    }

    if (purpose === TaskPurposeType.Operation) {
      return true;
    } else {
      const connectedIndexes = [];
      const itemServiceIds = _.compact(item.data.bundleServiceIdList || [item.data.serviceId]);

      drop.data.forEach((t, i) => {
        const serviceIds = _.compact(t.bundleServiceIdList || [t.serviceId]);

        if ((item.data.id !== t.id) && !!_.intersection(serviceIds, itemServiceIds).length) {
          connectedIndexes.push(i)
        }
      })

      if (connectedIndexes.length) {
        let canMove = true;

        if (purpose === TaskPurposeType.Pickup) {
          connectedIndexes.forEach((i) => {
            if (i <= index) {
              canMove = false;
            }
          })
          return canMove;
        }

        if (purpose === TaskPurposeType.Dropoff) {
          connectedIndexes.forEach((i) => {
            if (i >= index) {
              canMove = false;
            }
          })
          return canMove;
        }
      } else {
        return true;
      }
    }
  }

  // Precomputations
  private _precomputeTaskHelpers() {
    if (!this.tasks) return;

    this.isSplitAllowedMap = {};
    this.accountReferencesMap = {};
    this.proNumbersMap = {};
    this.relationsMap = {};

    this.tasks.forEach(task => {
      this.isSplitAllowedMap[task.id] = task.taskType !== TaskType.BundledStopover && this.servicesService.isSplitAllowedForStatus(task.status);
      this.relationsMap[task.id] = dedupeRelationsByService(task.routeTaskRelations || [], task.taskSpec?.purpose);

      // Accounts mapping
      const taskList = task.taskType === TaskType.BundledStopover
        ? (task.bundledTaskList || task.bundledRouteTaskList || [])
        : [];
      const accountReferences = taskList.length
        ? _.chain(taskList)
          .flatMap('accountReference')
          .compact()
          .uniqBy('id')
          .value()
        : [];
      const calculatedAccRefs = accountReferences.length
        ? accountReferences
        : task.accountReference
          ? [task.accountReference]
          : [];
      if (task.taskType === TaskType.BundledStopover) {
        const mappedAccRefs = calculatedAccRefs.map(accRef => {
          const totalPallets = task.bundledTaskList?.reduce((sum, bundledTask: TaskModel) => {
            if (bundledTask.accountReference?.id === accRef.id) {
              return sum + this._getPalletsFromPackageList(bundledTask.packageList || []);
            }
            return sum;
          }, 0) || 0;
          return { ...accRef, pallets: Number.isInteger(totalPallets) ? totalPallets : Number(totalPallets.toFixed(2)) };
        });

        if (mappedAccRefs.length === 1 && !mappedAccRefs[0].pallets) {
          const totalPallets = this._getPalletsFromPackageList(task.packageList || []);
          mappedAccRefs[0].pallets = totalPallets;
        }
        this.accountReferencesMap[task.id] = mappedAccRefs;
      } else {
        const totalPallets = this._getPalletsFromPackageList(task.packageList || []);
        this.accountReferencesMap[task.id] = calculatedAccRefs.map(accRef => ({ ...accRef, pallets: totalPallets }));
      }

      // Pro Numbers mapping
      let proNumbers: string[] = [];
      if (task.taskType === TaskType.BundledStopover && task.bundledRouteTaskList) {
        let proSet = new Set<string>();
        task.bundledRouteTaskList.forEach(bundled => {
          if (bundled.serviceInfo?.proNumber) {
            proSet.add(bundled.serviceInfo.proNumber);
          }
        });
        proNumbers = Array.from(proSet);
      } else if (task.serviceInfo?.proNumber) {
        proNumbers.push(task.serviceInfo.proNumber);
      }
      this.proNumbersMap[task.id] = proNumbers;
    });
  }

  private _getPalletsFromPackageList(packageList: any[]): number {
    let totalPallets = 0;
    if (Array.isArray(packageList) && packageList.length) {
      packageList.forEach(pkg => {
        if (pkg?.items?.length) {
          totalPallets += pkg.items.reduce((acc, item) => {
            if (item.pallets) {
              acc += (item.pallets || 0);
            }
            return acc;
          }, 0);
        }
      });
    }
    return Number.isInteger(totalPallets) ? totalPallets : Number(totalPallets.toFixed(2));
  }

  public trackById(_: number, item: TaskModel): string {
    return item.id;
  }
}
